import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { useAuth } from "../auth/auth-context";
import EntityList from "../entity-list/entity-list";
import { ApplicationListProps } from "./application-list.props";
import { ApplicationListViewModel } from "./application-list.view-model";
import { IApplicationModel } from "src/types/models/apiModels";
import ApplicationDownloads from "../application-downloads/application-downloads";
import DownloadButton from "src/components/atoms/download-button/download-button";
import { BusinessViewModel } from "../business-list/business-list.view-model";
import ActionPanel from "../action-panel/action-panel";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import Banner from "src/components/molecules/banner/Banner";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { ReactComponent as FlagIcon } from "../../../assets/svg/flagIcon.svg";
import { ReactComponent as FlagOffIcon } from "../../../assets/svg/flagOffIcon.svg";
import { ReactComponent as MonitorIcon } from "../../../assets/svg/monitorIcon.svg";
import { ReactComponent as MonitorOffIcon } from "../../../assets/svg/monitorOffIcon.svg";
import { ReactComponent as MobileIcon } from "../../../assets/svg/mobileIcon.svg";

const ApplicationList: React.FC<ApplicationListProps> = ({
	onAddNewClick,
	selectMode,
	onSelect,
}) => {
	const DELETEBRANDINGAPPLICATION = "DELETE BRANDING APPLICATION";
	const { user } = useAuth(); // Extract user from useAuth hook
	const [canAddNewBrandingApplication, setCanAddNewBrandingApplication] =
		useState(false);
	const [
		showApplicationDeleteConfirmation,
		setShowApplicationDeleteConfirmation,
	] = useState(false);
	const [data, setData] = useState<IApplicationModel[]>([]);
	const [refreshData, setRefreshData] = useState(false);
	const [loading, setLoading] = useState(true);
	const viewModel = useRef<ApplicationListViewModel | null>(null);
	const businessListViewModel = useRef<BusinessViewModel | null>(null);
	const [currentDialog, setCurrentDialog] = useState<{
		application: IApplicationModel | null;
		type: "CREDENTIALS" | "EDIT" | "DELETE" | null;
	}>({ application: null, type: null });
	const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
	const handleSelectApplication = (application: IApplicationModel) => {
		if (onSelect) {
			onSelect(application);
		}
	};
	const [applicationToDelete, setApplicationToDelete] =
		useState<IApplicationModel | null>(null);

	const toggleDeleteApplication = () => {
		setShowApplicationDeleteConfirmation((prevValue) => !prevValue);
	};
	const [applicationDeleteError, setApplicationDeleteError] = useState("");

	useEffect(() => {
		const canAddNewBrandingApplication = async () => {
			let hasApprovedBusiness = false;

			if (user && user.accessToken) {
				businessListViewModel.current = new BusinessViewModel(
					user.accessToken
				);

				const businesses =
					await businessListViewModel.current.fetchListAsync();

				hasApprovedBusiness =
					businesses.filter(
						(p) => p.status === "Active" || p.status === "Approved"
					).length > 0;
			}

			const userPermissions = user?.permissionBag.permissions;

			if (
				hasApprovedBusiness &&
				userPermissions &&
				userPermissions.filter(
					(p) => p.name === "BrandingApp" && p.value === "Create"
				).length > 0
			) {
				setCanAddNewBrandingApplication(true);
			} else {
				setCanAddNewBrandingApplication(false);
			}
		};

		canAddNewBrandingApplication();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setLoading((prevValue) => {
				if (!prevValue) {
					return true;
				}
				return prevValue;
			});
			try {
				if (user && user.accessToken) {
					viewModel.current = new ApplicationListViewModel(
						user.accessToken
					);
					const response = await viewModel.current?.fetchListAsync();
					setData(() => response);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				setLoading((prevValue) => {
					if (prevValue) {
						return false;
					}
					return prevValue;
				});
			}
		};

		fetchData();
		setRefreshData(false);
	}, [user, refreshData]);

	const renderBusinessItem = (application: IApplicationModel) => (
		<>
			<div className="flex  item-start justify-start w-full gap-4">
				<div className="flex items-center justify-start gap-2">
					<img
						className="w-12 h-12 drop-shadow-md rounded-full flex-shrink-0"
						src={application.business.logo?.signedUrl}
						alt=""
					></img>
					<div className="flex flex-col items-start justify-center gap-2">
						<span className="font-Poppins font-semibold min-w-96 text-left">
							{application.name ?? application.business.name}
						</span>
						<span
							className="font-Poppins font-semibold text-xs min-w-96 max-w-96 text-nowrap text-left overflow-hidden text-ellipsis whitespace-nowrap"
							title={application.description}
						>
							{application.description || "-"}
						</span>
					</div>
				</div>
				<div className="flex items-center justify-start gap-2">
					{/* Mobile application Section */}
					<span className="font-semibold text-sm bg-sky-600 p-2 px-2 text-white rounded drop-shadow-md">
						<MobileIcon className="w-4 h-4"></MobileIcon>
					</span>
					{/* Contact Center Section */}
					{application.contactCentre ? (
						<span className="font-semibold text-sm bg-green-600 p-2 px-2 text-white rounded drop-shadow-md">
							<MonitorIcon className="w-4 h-4"></MonitorIcon>
						</span>
					) : (
						<span className="font-semibold text-sm bg-red-600 p-2 px-2 text-white rounded drop-shadow-md">
							<MonitorOffIcon className="w-4 h-4"></MonitorOffIcon>
						</span>
					)}
					{application.brandingOption ===
					"CPaaSManagedCallBranding" ? (
						<span className="font-semibold text-sm bg-cyan-600 p-2 px-2 text-white rounded drop-shadow-md">
							<FlagIcon className="w-4 h-4"></FlagIcon>
						</span>
					) : (
						<span className="font-semibold text-sm bg-red-600 p-2 px-2 text-white rounded drop-shadow-md">
							<FlagOffIcon className="w-4 h-4"></FlagOffIcon>
						</span>
					)}
					{/* Downloads Section */}
					{application.status === "Active" && (
						<span className="font-semibold text-sm bg-indigo-600 p-2 px-2 text-white rounded drop-shadow-md">
							<DownloadButton
								text="Download Credentials"
								onClick={() =>
									setCurrentDialog({
										application,
										type: "CREDENTIALS",
									})
								}
								type="Icon"
							></DownloadButton>
						</span>
					)}
				</div>
			</div>
		</>
	);

	if (loading) {
		return (
			<LoadingModal
				message="Loading your branding applications, please hold on!"
				open={loading}
			/>
		); // Pass the optional message
	}

	return (
		<>
			<div className="flex flex-col items-start justify-center gap-8">
				<EntityList<IApplicationModel>
					data={data}
					selectMode={selectMode} // or false depending on your requirements
					onItemSelected={(selectedBusiness) =>
						handleSelectApplication(selectedBusiness)
					}
					onEditClicked={(selectedBusiness) =>
						handleSelectApplication(selectedBusiness)
					}
					renderChildren={renderBusinessItem}
					onAddNewClick={() => {
						onAddNewClick();
					}}
					onDeleteClicked={(applicationToDelete) => {
						setApplicationToDelete(applicationToDelete);
						toggleDeleteApplication();
					}}
					canAddNew={canAddNewBrandingApplication}
					listLabel={"Choose Your Application"}
					filterText="Filter on business name"
					enableView={false}
					enableDelete={true}
				/>
				{/* <div className="bg-slate-100 flex flex-col items-center justify-center gap-6 p-4">
					<span className="font-Poppins font-semibold text-sm w-full p-2">
						Legend
					</span>
					<div className="flex items-start justify-center gap-4">
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-semibold text-sm bg-sky-600 p-2 px-2 text-white rounded drop-shadow-md">
								<MobileIcon className="w-4 h-4"></MobileIcon>
							</span>
							<span className="font-Poppins font-semibold text-sm">
								Mobile application
							</span>
						</div>
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-semibold text-sm bg-green-600 p-2 px-2 text-white rounded drop-shadow-md">
								<MonitorIcon className="w-4 h-4"></MonitorIcon>
							</span>
							<span className="font-Poppins font-semibold text-sm">
								Contact center
							</span>
						</div>
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-semibold text-sm bg-red-600 p-2 px-2 text-white rounded drop-shadow-md">
								<MonitorOffIcon className="w-4 h-4"></MonitorOffIcon>
							</span>
							<span className="font-Poppins font-semibold text-sm">
								No contact center
							</span>
						</div>
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-semibold text-sm bg-cyan-600 p-2 px-2 text-white rounded drop-shadow-md">
								<FlagIcon className="w-4 h-4"></FlagIcon>
							</span>
							<span className="font-Poppins font-semibold text-sm">
								Managed application
							</span>
						</div>
						<div className="flex flex-col items-center justify-center gap-4">
							<span className="font-semibold text-sm bg-red-600 p-2 px-2 text-white rounded drop-shadow-md">
								<FlagOffIcon className="w-4 h-4"></FlagOffIcon>
							</span>
							<span className="font-Poppins font-semibold text-sm">
								Unmanaged application
							</span>
						</div>
					</div>
				</div> */}
			</div>
			{/* Application delete confirmation dialog */}
			<Dialog
				open={
					applicationToDelete != null &&
					showApplicationDeleteConfirmation
				}
				onClose={() => {
					toggleDeleteApplication();
					setApplicationToDelete(null);
					setDeleteConfirmationText("");
					setApplicationDeleteError("");
				}}
				maxWidth="md"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Banner
						backgroundColor="bg-red-600"
						label="Delete Branding Application"
						buttonBorderColor="border-white"
					>
						<div className="bg-red-600 flex flex-col items-center justify-center gap-6">
							<span className="font-Poppins font-semibold text-xl text-white">
								Are you sure you want to delete the branding
								application?
							</span>
							<div className="bg-slate-50 flex flex-col items-center justify-center gap-4 w-full pt-4 p-4">
								<InputBox
									label={`Enter the ${DELETEBRANDINGAPPLICATION} to confirm`}
									type={"text"}
									value={deleteConfirmationText}
									onChange={function (value: string): void {
										setDeleteConfirmationText(value);
									}}
								></InputBox>

								<ActionPanel
									primaryButtonLabel={"Yes"}
									primaryButtonEnabled={
										deleteConfirmationText ===
										DELETEBRANDINGAPPLICATION
									}
									secondaryButtonLabel={"No"}
									secondaryButtonEnabled={true}
									primaryButtonAction={async function (): Promise<void> {
										if (applicationToDelete) {
											try {
												const result =
													await viewModel.current?.deleteBrandingApplicationAsync(
														applicationToDelete
													);
												setApplicationToDelete(null);
												setDeleteConfirmationText("");
												toggleDeleteApplication();
												setApplicationDeleteError("");
												console.log(
													"Delete result:",
													result
												);
												if (result) {
													setRefreshData(true);
												}
											} catch (error) {
												setApplicationDeleteError(
													"Failed to delete application"
												);
												console.error(
													"Failed to delete application:",
													error
												);
											}
										}
									}}
									secondaryButtonAction={() => {
										setApplicationToDelete(null);
										setDeleteConfirmationText("");
										setApplicationDeleteError("");
										toggleDeleteApplication();
									}}
								/>
							</div>
						</div>
					</Banner>
				</DialogContent>
			</Dialog>
			{/* Dialog for Downloads */}
			<Dialog
				open={currentDialog.type === "CREDENTIALS"}
				onClose={() =>
					setCurrentDialog({ application: null, type: null })
				}
				maxWidth="lg"
				fullWidth
				PaperProps={{
					style: {
						backgroundColor: "transparent",
						boxShadow: "none",
						width: "100%",
						alignContent: "centre",
						justifyContent: "center",
					},
				}}
			>
				<DialogContent
					style={{
						padding: 0,
						margin: 0,
						width: "100%", // Ensure it takes up the full width
						display: "flex",
						justifyContent: "center",
					}}
				>
					{currentDialog.application && (
						<ApplicationDownloads
							application={currentDialog.application}
							onClose={() =>
								setCurrentDialog({
									application: null,
									type: null,
								})
							}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ApplicationList;
